import { Component } from '@theme/component';
import { createWishlistStore } from '@theme/charm-wishlist-store';

const CHANGE_EVENT = 'charm:wishlist-change';

/** Uma instância por página: todos os corações e a lista compartilham o mesmo estado. */
const store = createWishlistStore(
  (() => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  })()
);

// Voltar do bfcache ou mudança em outra aba: o store relê o storage, só falta redesenhar.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});
window.addEventListener('storage', (event) => {
  if (event.key === null || event.key === 'charm:wishlist') document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});

/**
 * Coração de favoritar. Atributo data-handle = handle do produto.
 * @extends {Component<{}>}
 */
class CharmWishButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.#render);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#render);
  }

  /** @param {Event} event */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    store.toggle(this.dataset.handle);
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }

  #render = () => {
    const on = store.has(this.dataset.handle);
    this.toggleAttribute('data-active', on);
    this.querySelector('button')?.setAttribute('aria-pressed', String(on));
  };
}

const money = (cents) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: window.Shopify?.currency?.active ?? 'BRL' }).format(
    cents / 100
  );

/**
 * Lista da página de favoritos: busca cada produto pela Ajax API e ignora os que não existem mais.
 * @typedef {{list: HTMLElement, empty: HTMLElement}} Refs
 * @extends {Component<Refs>}
 */
class CharmWishlist extends Component {
  requiredRefs = ['list', 'empty'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CHANGE_EVENT, this.#load);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#load);
  }

  #load = async () => {
    const root = window.Shopify?.routes?.root ?? '/';
    const results = await Promise.all(
      store.list().map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );
    const products = results.filter(Boolean);
    this.refs.list.replaceChildren(...products.map((product) => this.#card(product)));
    this.refs.empty.hidden = products.length > 0;
  };

  /** Monta o card com DOM APIs (textContent), sem HTML interpolado. */
  #card(product) {
    const item = document.createElement('li');
    item.className = 'charm-wishlist__item';
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'charm-wishlist__link';
    if (product.featured_image) {
      const img = document.createElement('img');
      img.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=400`;
      img.alt = product.title;
      img.loading = 'lazy';
      link.append(img);
    }
    const title = document.createElement('span');
    title.className = 'charm-wishlist__title';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'charm-wishlist__price';
    price.textContent = money(product.price);
    link.append(title, price);

    const heart = document.createElement('charm-wish-button-component');
    heart.className = 'charm-wish';
    heart.dataset.handle = product.handle;
    // markup fixo (sem dado do produto): mesmo coração do snippet charm-wish-button
    heart.innerHTML =
      '<button type="button" class="charm-wish__btn button-unstyled" on:click="/toggle" aria-label="Remover dos favoritos">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
      '<path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/></svg></button>';
    item.append(link, heart);
    return item;
  }
}

if (!customElements.get('charm-wish-button-component')) {
  customElements.define('charm-wish-button-component', CharmWishButton);
}
if (!customElements.get('charm-wishlist-component')) {
  customElements.define('charm-wishlist-component', CharmWishlist);
}
